import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Search, MapPin, Clock, Calendar, Phone, Star, ChevronDown, ChevronUp, Filter, X, Check, Mail, Hash, Landmark, Building2, User, Shield, ArrowRight, Sun, Moon, Navigation, RotateCcw, Crosshair, Stethoscope, Info, ReceiptText } from 'lucide-react';
import toothlensLogo from "../../assets/illustrations/Toothlens Logo.jpeg";
import { getInsuranceClinicList, getInsuranceClinicAvailability, createInsuranceAppointment, requestDentistByPincode, referDentist } from '../../Services/APIs/patient';
import { getLocalDateString } from '../../Utils/timeSlots';
import { isDevPortal } from '../../Utils/devPortal';
import { canClaimReimbursement as canClaimReimbursementFor } from '../PhonepePortal/phonepeSession';

/* ───────────────────────────────────────────
   Dark Mode context via CSS custom properties
   ─────────────────────────────────────────── */
const themeStyles = {
  light: {
    '--bg-primary': '#fafaff',
    '--bg-secondary': '#f4f6fb',
    '--bg-tertiary': '#eef1f8',
    '--bg-card': '#ffffff',
    '--bg-input': '#f4f6fb',
    '--bg-hover': '#eef1f8',
    '--text-primary': '#1a1d2e',
    '--text-secondary': '#5b6278',
    '--text-tertiary': '#8c93a8',
    '--border-color': '#e0e4ef',
    '--border-light': '#eef1f8',
    '--shadow-card': '0 2px 8px rgba(99,102,241,0.06), 0 1px 3px rgba(0,0,0,0.04)',
    '--shadow-card-hover': '0 12px 28px rgba(99,102,241,0.12), 0 4px 12px rgba(0,0,0,0.05)',
    '--shadow-elevated': '0 20px 40px rgba(99,102,241,0.15)',
  },
  dark: {
    '--bg-primary': '#0c1222',
    '--bg-secondary': '#141c30',
    '--bg-tertiary': '#1c2742',
    '--bg-card': '#141c30',
    '--bg-input': '#1c2742',
    '--bg-hover': '#1c2742',
    '--text-primary': '#e8ecf4',
    '--text-secondary': '#8b9dc3',
    '--text-tertiary': '#5a6d93',
    '--border-color': '#1f2d4a',
    '--border-light': '#141c30',
    '--shadow-card': '0 2px 8px rgba(59,130,246,0.08), 0 1px 3px rgba(0,0,0,0.3)',
    '--shadow-card-hover': '0 12px 30px rgba(59,130,246,0.15), 0 4px 12px rgba(0,0,0,0.3)',
    '--shadow-elevated': '0 20px 40px rgba(59,130,246,0.2), 0 8px 16px rgba(0,0,0,0.4)',
  }
};

/* ───────────────────────────────────────────
   Sub-components
   ─────────────────────────────────────────── */

// Dark mode toggle button
const DarkModeToggle = ({ isDark, onToggle }) => (
  <button
    onClick={onToggle}
    className="relative w-14 h-7 rounded-full transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
    style={{ background: isDark ? '#334155' : '#e2e8f0' }}
    aria-label="Toggle dark mode"
  >
    <div
      className="absolute top-0.5 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-500 shadow-md"
      style={{
        left: isDark ? '30px' : '2px',
        background: isDark ? '#1e293b' : '#ffffff',
      }}
    >
      {isDark ? <Moon className="w-3.5 h-3.5 text-blue-400" /> : <Sun className="w-3.5 h-3.5 text-amber-500" />}
    </div>
  </button>
);

// Booking Modal Component
const BookingModal = ({
  showBooking, setShowBooking, selectedClinic, userDetails, setUserDetails,
  selectedDate, setSelectedDate, selectedTime, setSelectedTime,
  selectedAppointmentType, setSelectedAppointmentType,
  handleBookAppointment, bookingLoading, bookingError, timeSlots, isDark
}) => {
  if (!showBooking) return null;
  const cardBg = isDark ? '#1e293b' : '#ffffff';
  const textPrimary = isDark ? '#f1f5f9' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const inputBg = isDark ? '#334155' : '#f8fafc';
  const inputBorder = isDark ? '#475569' : '#e2e8f0';
  const subCardBg = isDark ? '#334155' : '#f0f9ff';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.6)', animation: 'fadeIn 0.3s ease-out' }}>
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl shadow-2xl" style={{ background: cardBg, animation: 'slideUp 0.4s ease-out' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 rounded-t-3xl p-4 flex justify-between items-center" style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Book Appointment</h3>
              <p className="text-blue-200 text-xs">Fill in the details below</p>
            </div>
          </div>
          <button onClick={() => setShowBooking(false)} className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Clinic Info */}
          <div className="p-4 rounded-2xl" style={{ background: subCardBg, animation: 'fadeInUp 0.5s ease-out' }}>
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: isDark ? '#2563eb' : '#dbeafe' }}>
                <Building2 className="w-5 h-5" style={{ color: isDark ? '#fff' : '#2563eb' }} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm" style={{ color: isDark ? '#93c5fd' : '#1e40af' }}>{selectedClinic?.name}</h4>
                <div className="space-y-0.5 mt-1 text-xs" style={{ color: isDark ? '#93c5fd' : '#3b82f6' }}>
                  {selectedClinic?.network_partner?.name && <p className="flex items-center"><User className="w-3 h-3 mr-1" />{selectedClinic.network_partner.name}</p>}
                  {selectedClinic?.network_partner?.experience_years && <p className="flex items-center"><Star className="w-3 h-3 mr-1" />{selectedClinic.network_partner.experience_years} years exp.</p>}
                  {selectedClinic?.address && <p className="flex items-center"><MapPin className="w-3 h-3 mr-1 flex-shrink-0" /><span className="truncate">{selectedClinic.address}</span></p>}
                </div>
              </div>
            </div>
          </div>

          {/* Patient Details */}
          <div className="space-y-3" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <h4 className="font-semibold text-sm flex items-center" style={{ color: textPrimary }}>
              <User className="w-4 h-4 mr-2 text-blue-500" />Patient Details
            </h4>
            <div className="rounded-xl p-3" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: textSecondary }}>Name</label>
              <div className="flex items-center mt-1">
                <User className="w-4 h-4 text-blue-400 mr-2" />
                <p className="font-medium text-sm" style={{ color: textPrimary }}>{userDetails.name || '—'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl p-3" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
                <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: textSecondary }}>Email</label>
                <div className="flex items-center mt-1">
                  <Mail className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                  <p className="font-medium text-sm truncate" style={{ color: textPrimary }}>{userDetails.email || '—'}</p>
                </div>
              </div>
              <div className="rounded-xl p-3" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
                <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: textSecondary }}>Mobile</label>
                <div className="flex items-center mt-1">
                  <Phone className="w-4 h-4 text-blue-400 mr-2" />
                  <p className="font-medium text-sm" style={{ color: textPrimary }}>{userDetails.mobile || '—'}</p>
                </div>
              </div>
            </div>
          </div>


          {/* Appointment Type */}
          <div style={{ animation: 'fadeInUp 0.65s ease-out' }}>
            <label className="block text-sm font-semibold mb-2 flex items-center" style={{ color: textPrimary }}>
              <Stethoscope className="w-4 h-4 mr-2 text-blue-500" />Appointment Type <span className="text-red-500 ml-1">*</span>
            </label>
            <select value={selectedAppointmentType} onChange={e => setSelectedAppointmentType(e.target.value)}
              className="w-full p-3 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: textPrimary }}>
              <option value="" disabled>Select appointment type</option>
              <option value="consultation">Consultation</option>
              <option value="procedure">Procedure</option>
            </select>
          </div>

          {/* Date */}
          <div style={{ animation: 'fadeInUp 0.7s ease-out' }}>
            <label className="block text-sm font-semibold mb-2 flex items-center" style={{ color: textPrimary }}>
              <Calendar className="w-4 h-4 mr-2 text-blue-500" />Select Date <span className="text-red-500 ml-1">*</span>
            </label>
            <input type="date" value={selectedDate}
              onChange={(e) => {
                const v = e.target.value;
                const today = getLocalDateString();
                if (v < today) { window.alert('Past dates cannot be booked.'); setSelectedDate(''); setSelectedTime(''); }
                else { setSelectedDate(v); setSelectedTime(''); }
              }}
              min={getLocalDateString()}
              className="w-full p-3 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: textPrimary }}
            />
          </div>

          {/* Time */}
          <div style={{ animation: 'fadeInUp 0.8s ease-out' }}>
            <label className="block text-sm font-semibold mb-2 flex items-center" style={{ color: textPrimary }}>
              <Clock className="w-4 h-4 mr-2 text-blue-500" />Select Time <span className="text-red-500 ml-1">*</span>
            </label>
            {timeSlots.length > 0 ? (
              <select value={selectedTime} onChange={e => setSelectedTime(e.target.value)}
                className="w-full p-3 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: textPrimary }}>
                <option value="" disabled>Select a time slot</option>
                {timeSlots.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            ) : (
              <div className="w-full p-3 rounded-xl text-center text-sm" style={{ background: inputBg, border: `1px solid ${inputBorder}`, color: textSecondary }}>
                {selectedDate ? <span className="flex items-center justify-center"><X className="w-4 h-4 mr-2 text-orange-400" />No slots on this date</span> : 'Please select a date first'}
              </div>
            )}
          </div>

          {/* Confirm */}
          <button onClick={handleBookAppointment}
            className="w-full text-white py-4 rounded-xl font-semibold text-base transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 4px 15px rgba(37,99,235,0.3)', animation: 'fadeInUp 0.9s ease-out' }}
            disabled={!selectedDate || !selectedTime || !selectedAppointmentType || !userDetails.name || !userDetails.email || !userDetails.mobile || bookingLoading}>
            {bookingLoading ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Booking...</span></>) : (<><Check className="w-5 h-5" /><span>Confirm Appointment</span></>)}
          </button>
          {bookingError && <div className="text-center text-red-500 font-semibold p-3 rounded-xl text-sm" style={{ background: isDark ? '#371520' : '#fef2f2', border: `1px solid ${isDark ? '#5b2130' : '#fecaca'}`, animation: 'shake 0.5s ease-out' }}>{bookingError}</div>}
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ showConfirmation, setShowConfirmation, bookedAppointment, setHasSearched, companyName, isDark, onClose }) => {
  if (!showConfirmation) return null;
  const cardBg = isDark ? '#1e293b' : '#ffffff';
  const textPrimary = isDark ? '#f1f5f9' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: isDark ? '#0f172a' : 'linear-gradient(180deg, #f8fafc, #ffffff)' }}>
      {/* Header */}
      <div className="text-white px-6 py-5 flex items-center justify-between shadow-lg" style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}>
        <div>
          <h1 className="text-xl font-bold">ToothLens</h1>
          <p className="text-blue-200 text-xs mt-0.5">Appointment Confirmed</p>
        </div>
        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md">
          <img src={toothlensLogo} alt="ToothLens" className="w-6 h-6 rounded" />
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center px-5 py-8">
        {/* Success icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5" style={{ boxShadow: '0 8px 25px rgba(34,197,94,0.2)', animation: 'scaleIn 0.5s ease-out' }}>
          <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center" style={{ animation: 'scaleIn 0.7s ease-out' }}>
            <Check className="w-8 h-8 text-white" />
          </div>
        </div>
        <h2 className="text-lg font-bold mb-2 text-center" style={{ color: isDark ? '#86efac' : '#15803d', animation: 'fadeInUp 0.6s ease-out' }}>Appointment Request Submitted!</h2>
        <p className="text-sm mb-6 text-center max-w-sm" style={{ color: isDark ? '#4ade80' : '#16a34a', animation: 'fadeInUp 0.7s ease-out' }}>Our Case Manager will contact you shortly to confirm your appointment.</p>

        {/* Details */}
        <div className="rounded-2xl p-5 w-full max-w-md mb-4 shadow-md" style={{ background: cardBg, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, animation: 'fadeInUp 0.8s ease-out' }}>
          <h3 className="font-bold mb-3 flex items-center text-sm" style={{ color: textPrimary }}><Calendar className="w-4 h-4 mr-2 text-blue-500" />Appointment Details</h3>
          <div className="space-y-2.5">
            {[
              ['Patient', bookedAppointment?.userDetails?.name],
              ['Clinic', <><span className="font-semibold">{bookedAppointment?.clinic?.name}</span><br/><span style={{ color: textSecondary }} className="text-xs">{bookedAppointment?.clinic?.doctor}</span></>],
              ['Date', bookedAppointment?.date ? new Date(bookedAppointment.date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : ''],
              ['Time', <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">{bookedAppointment?.time}</span>],
              ['Type', <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold capitalize">{bookedAppointment?.appointmentType}</span>],
            ].map(([label, val], i) => (
              <div key={i}>
                <div className="flex justify-between items-start">
                  <span className="text-xs" style={{ color: textSecondary }}>{label}</span>
                  <span className="font-semibold text-sm text-right" style={{ color: textPrimary }}>{val}</span>
                </div>
                {i < 4 && <div className="border-t mt-2" style={{ borderColor: isDark ? '#334155' : '#f1f5f9' }}></div>}
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="rounded-2xl p-5 w-full max-w-md mb-4" style={{ background: isDark ? '#1e3a5f' : 'linear-gradient(135deg, #eff6ff, #eef2ff)', border: `1px solid ${isDark ? '#1e40af' : '#bfdbfe'}`, animation: 'fadeInUp 0.9s ease-out' }}>
          <h4 className="font-semibold mb-3 text-sm flex items-center" style={{ color: isDark ? '#93c5fd' : '#1e40af' }}><Phone className="w-4 h-4 mr-2" />Contact Information</h4>
          <div className="space-y-2 text-sm">
            <div className="rounded-xl p-3 space-y-1.5" style={{ background: isDark ? '#0f172a80' : 'rgba(255,255,255,0.6)' }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDark ? '#60a5fa' : '#1e40af' }}>Your Details</p>
              <div className="flex items-center" style={{ color: isDark ? '#cbd5e1' : '#374151' }}><Mail className="w-3.5 h-3.5 mr-2 text-blue-400" /><span className="text-xs">{bookedAppointment?.userDetails?.email}</span></div>
              <div className="flex items-center" style={{ color: isDark ? '#cbd5e1' : '#374151' }}><Phone className="w-3.5 h-3.5 mr-2 text-blue-400" /><span className="text-xs">{bookedAppointment?.userDetails?.mobile}</span></div>
            </div>
            <div className="rounded-xl p-3 space-y-1.5" style={{ background: isDark ? '#0f172a80' : 'rgba(255,255,255,0.6)' }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isDark ? '#60a5fa' : '#1e40af' }}>Clinic</p>
              {bookedAppointment?.clinic?.name && <div className="flex items-center" style={{ color: isDark ? '#cbd5e1' : '#374151' }}><Building2 className="w-3.5 h-3.5 mr-2 text-blue-400" /><span className="text-xs">{bookedAppointment.clinic.name}</span></div>}
              {bookedAppointment?.clinic?.address && <div className="flex items-center" style={{ color: isDark ? '#cbd5e1' : '#374151' }}><MapPin className="w-3.5 h-3.5 mr-2 text-blue-400" /><span className="text-xs">{bookedAppointment.clinic.address}</span></div>}
              {bookedAppointment?.clinic?.city && <div className="flex items-center" style={{ color: isDark ? '#cbd5e1' : '#374151' }}><MapPin className="w-3.5 h-3.5 mr-2 text-blue-400" /><span className="text-xs">{[bookedAppointment.clinic.city, bookedAppointment.clinic.state, bookedAppointment.clinic.pincode].filter(Boolean).join(', ')}</span></div>}
            </div>
          </div>
        </div>

        {/* Notes + Done */}
        <div className="rounded-2xl p-5 w-full max-w-md mb-5" style={{ background: isDark ? '#422006' : '#fffbeb', border: `1px solid ${isDark ? '#92400e' : '#fde68a'}`, animation: 'fadeInUp 1s ease-out' }}>
          <h4 className="font-semibold mb-2 text-sm flex items-center" style={{ color: isDark ? '#fbbf24' : '#92400e' }}><Shield className="w-4 h-4 mr-2" />Important Notes</h4>
          <ul className="text-xs space-y-1.5" style={{ color: isDark ? '#fcd34d' : '#a16207' }}>
            <li className="flex items-start"><span className="mr-2">●</span>Please arrive 15 minutes before your appointment</li>
            <li className="flex items-start"><span className="mr-2">●</span>Please show your ToothLens UID at the clinic</li>
            <li className="flex items-start"><span className="mr-2">●</span>Contact ToothLens for appointment assistance</li>
          </ul>
        </div>

        <div className="w-full max-w-md" style={{ animation: 'fadeInUp 1.1s ease-out' }}>
          <button onClick={() => { if (onClose) onClose(); else if (window.parent !== window) window.parent.postMessage('closeAppointment', '*'); else window.location.href = '/'; }}
            className="w-full text-white py-3.5 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 4px 15px rgba(37,99,235,0.3)' }}>
            <Check className="w-5 h-5" /><span>Done</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Refer Your Dentist Modal Component - native replacement for the external Tally form
// (https://tally.so/r/XxD8ye) that used to be linked from the empty clinic-results screen.
// Unlike BookingModal/ConfirmationModal, this owns its form state internally rather than being
// controlled via props - nothing outside this modal needs the referral form's field values.
const ReferDentistModal = ({ show, onClose, userDetails, companyName, isDark }) => {
  const [form, setForm] = useState({
    referrer_name: '', referrer_email: '', referrer_phone: '',
    dentist_name: '', clinic_name: '', clinic_phone: '', clinic_address: '', city: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Prefill "Your Details" from the logged-in member whenever the modal opens.
  useEffect(() => {
    if (show) {
      setForm(f => ({
        ...f,
        referrer_name: userDetails?.name || f.referrer_name,
        referrer_email: userDetails?.email || f.referrer_email,
        referrer_phone: userDetails?.mobile || f.referrer_phone,
      }));
    }
  }, [show, userDetails]);

  if (!show) return null;

  const cardBg = isDark ? '#1e293b' : '#ffffff';
  const textPrimary = isDark ? '#f1f5f9' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const inputBg = isDark ? '#334155' : '#f8fafc';
  const inputBorder = isDark ? '#475569' : '#e2e8f0';

  const setField = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const isValid = Object.values(form).every(v => v.trim().length > 0)
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.referrer_email);

  const handleClose = () => {
    setForm({ referrer_name: '', referrer_email: '', referrer_phone: '', dentist_name: '', clinic_name: '', clinic_phone: '', clinic_address: '', city: '' });
    setSubmitted(false);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError('');

    try {
      await referDentist({
        ...form,
        requested_at: new Date().toISOString(),
        uid: userDetails?.uid || null,
        client: companyName || null,
        source: 'appointment_no_clinics',
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting dentist referral:', err);
      setError('Could not submit your referral. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = { background: inputBg, border: `1px solid ${inputBorder}`, color: textPrimary };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.6)', animation: 'fadeIn 0.3s ease-out' }}>
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl shadow-2xl" style={{ background: cardBg, animation: 'slideUp 0.4s ease-out' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 rounded-t-3xl p-4 flex justify-between items-center" style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Refer Your Dentist</h3>
              <p className="text-blue-200 text-xs">We'll reach out to enable cashless services</p>
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" style={{ animation: 'scaleIn 0.5s ease-out' }}>
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h4 className="font-bold text-base mb-1.5" style={{ color: textPrimary }}>Thank you for the referral!</h4>
            <p className="text-sm mb-6" style={{ color: textSecondary }}>Our team will connect with {form.dentist_name} and get back to you shortly.</p>
            <button onClick={handleClose}
              className="w-full text-white py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 4px 15px rgba(37,99,235,0.3)' }}>
              Done
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Your Details */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm flex items-center" style={{ color: textPrimary }}>
                <User className="w-4 h-4 mr-2 text-blue-500" />Your Details
              </h4>
              <input type="text" placeholder="Your name" value={form.referrer_name} onChange={setField('referrer_name')}
                className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
              <div className="grid grid-cols-2 gap-3">
                <input type="email" placeholder="Your email" value={form.referrer_email} onChange={setField('referrer_email')}
                  className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
                <input type="tel" placeholder="Your phone" value={form.referrer_phone} onChange={setField('referrer_phone')}
                  className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
              </div>
            </div>

            {/* Your Dentist's Details */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm flex items-center" style={{ color: textPrimary }}>
                <Stethoscope className="w-4 h-4 mr-2 text-blue-500" />Your Dentist's Details
              </h4>
              <input type="text" placeholder="Dentist name" value={form.dentist_name} onChange={setField('dentist_name')}
                className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
              <input type="text" placeholder="Clinic name" value={form.clinic_name} onChange={setField('clinic_name')}
                className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
              <div className="grid grid-cols-2 gap-3">
                <input type="tel" placeholder="Clinic phone" value={form.clinic_phone} onChange={setField('clinic_phone')}
                  className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
                <input type="text" placeholder="City" value={form.city} onChange={setField('city')}
                  className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
              </div>
              <input type="text" placeholder="Clinic address" value={form.clinic_address} onChange={setField('clinic_address')}
                className="w-full p-3 rounded-xl text-sm focus:outline-none" style={fieldStyle} />
            </div>

            <button onClick={handleSubmit}
              className="w-full text-white py-4 rounded-xl font-semibold text-base transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)', boxShadow: '0 4px 15px rgba(37,99,235,0.3)' }}
              disabled={!isValid || submitting}>
              {submitting ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Submitting...</span></>) : (<span>Submit Referral</span>)}
            </button>
            {error && <div className="text-center text-red-500 font-semibold p-3 rounded-xl text-sm" style={{ background: isDark ? '#371520' : '#fef2f2', border: `1px solid ${isDark ? '#5b2130' : '#fecaca'}`, animation: 'shake 0.5s ease-out' }}>{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

const getHighlightedText = (text, highlight) => {
  if (!highlight || !text) return text;
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
  return (<span>{parts.map((part, i) => part.toLowerCase() === highlight.toLowerCase() ? <span key={i} className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">{part}</span> : part)}</span>);
};

// Main Component
const PlumAppointment = ({ prefillData, onClose }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const { company } = useParams();

  // Extract company from current URL if useParams doesn't work
  const getCompanyFromUrl = () => {
    const currentPath = window.location.pathname;
    // Match patterns like /plum-appointment, /loop-appointment
    const match = currentPath.match(/\/([^-]+)-appointment/);
    return match ? match[1] : null;
  };

  const companyName = company || getCompanyFromUrl();
  const companyLower = companyName?.toLowerCase();
  const navigate = useNavigate();
  // Members whose plan includes reimbursement can consult any dentist instead — on their own
  // company's booking page only.
  const canClaimReimbursement = canClaimReimbursementFor(companyLower);

  // The "Testing" clinic is a test record returned by the availability API.
  // Show it for the demo login and on the dev portal (so bookings can be tested there);
  // hide it for all real users in production.
  const isDemoUser =
    (localStorage.getItem('insuranceLoginEmail') || '').toLowerCase() === 'demo@toothlens.com';
  const showTestClinics = isDemoUser || isDevPortal();
  const filterDemoClinics = (list) =>
    showTestClinics ? list : list.filter(c => (c.name || '').trim().toLowerCase() !== 'testing');

  // Define city filtering logic based on company
  const getFilteredCitiesByCompany = () => {
    return null; // Show all cities for all companies
  };
  const [filters, setFilters] = useState({
    location: '',
    pincode: '',
    city: '',
    doctorName: '',
    clinicName: '',
    latitude: null,
    longitude: null
  });

  // Applied filters - only updated when Search button is clicked
  const [appliedFilters, setAppliedFilters] = useState({
    location: '',
    pincode: '',
    city: '',
    doctorName: '',
    clinicName: '',
    latitude: null,
    longitude: null
  });

  // Which criterion the availability API actually applied. The API honours exactly one
  // and silently drops the rest. `filter_applied` is absent in production today, so
  // default to 'pincode' — the current server-side precedence.
  const [appliedFilterType, setAppliedFilterType] = useState('pincode');

  const [hasSearched, setHasSearched] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);
  const [totalClinics, setTotalClinics] = useState(0);

  // Clinics state to hold API data
  const [clinics, setClinics] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [clinicApiError, setClinicApiError] = useState("");
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(true);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  // "Request a dentist in my pincode" form, shown when a search returns no clinics
  const [requestPincode, setRequestPincode] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [requestError, setRequestError] = useState('');
  // "Refer your dentist" modal, replacing the external Tally form
  const [showReferDentist, setShowReferDentist] = useState(false);
  // Holds the "thank you -> back to search" timer so it can be cancelled
  const resetTimerRef = useRef(null);

  // New state for user details
  const [userDetails, setUserDetails] = useState({
    name: '',
    email: '',
    mobile: ''
  });

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedAppointmentType, setSelectedAppointmentType] = useState('');
  const [bookingError, setBookingError] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);

  // Function to parse time string and convert to 24-hour format
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const match = timeStr.match(/(\d{1,2})[:.](\d{2})\s*(am|pm)/i);
    if (!match) return null;

    let hour = parseInt(match[1]);
    const minute = parseInt(match[2]);
    const period = match[3].toLowerCase();

    if (period === 'pm' && hour !== 12) hour += 12;
    if (period === 'am' && hour === 12) hour = 0;

    return hour * 60 + minute; // Return minutes from midnight
  };

  // Function to generate time slots for a specific clinic
  const generateTimeSlots = (clinic) => {
    if (!clinic || !clinic.working_hours) return [];

    const slots = [];
    // Normalize en-dashes (–) and em-dashes (—) to regular hyphens before parsing
    const workingHours = clinic.working_hours.replace(/[\u2013\u2014]/g, '-');

    // Parse working hours (assuming format like "10:00 am-1:30 pm & 5:00 pm-8:30 pm")
    const timeRanges = workingHours.split('&').map(range => range.trim());

    timeRanges.forEach(range => {
      const [startTime, endTime] = range.split('-').map(t => t.trim());
      const startMinutes = parseTime(startTime);
      const endMinutes = parseTime(endTime);

      if (startMinutes !== null && endMinutes !== null && startMinutes < endMinutes) {
        // Generate 15-minute intervals
        for (let minutes = startMinutes; minutes < endMinutes; minutes += 15) {
          const hour = Math.floor(minutes / 60);
          const min = minutes % 60;

          let displayHour = hour;
          let period = 'AM';

          if (hour >= 12) {
            period = 'PM';
            if (hour > 12) displayHour = hour - 12;
          }
          if (hour === 0) displayHour = 12;

          const minuteString = min === 0 ? '00' : min.toString().padStart(2, '0');
          slots.push(`${displayHour}:${minuteString} ${period}`);
        }
      }
    });

    return slots;
  };

  // Get available time slots for selected clinic
  const getAvailableTimeSlots = (appointmentDate) => {
    if (!selectedClinic || !appointmentDate) return [];

    const selectedDateObj = new Date(appointmentDate);
    const dayOfWeek = selectedDateObj.getDay(); // 0 = Sunday, 6 = Saturday

    // Check if it's Sunday and clinic has Sunday hours
    let slots;
    if (dayOfWeek === 0) {
      slots = selectedClinic.sunday_hours && selectedClinic.sunday_hours !== 'Closed'
        ? generateTimeSlots({ working_hours: selectedClinic.sunday_hours })
        : [];
    } else {
      // For Monday-Saturday, use regular working hours
      slots = generateTimeSlots(selectedClinic);
    }

    // Require at least a 2-hour lead time for slots booked on today's date
    if (appointmentDate === getLocalDateString()) {
      const now = new Date();
      const cutoff = now.getHours() * 60 + now.getMinutes() + 120;
      return slots.filter(t => parseTime(t) >= cutoff);
    }
    return slots;
  };

  useEffect(() => {
    // Fetch available cities from API
    const fetchCities = async () => {
      setIsLoadingCities(true);

      try {
        const response = await getInsuranceClinicList();

        if (response.status === 200 && response.data?.data) {
          const citiesData = response.data.data;

          // Cities are now returned directly as an array of strings
          // Normalize and deduplicate cities (case-insensitive)
          const cityMap = new Map();
          citiesData.forEach(city => {
            if (city && city.trim() !== '') {
              const cityLower = city.toLowerCase().trim();
              if (!cityMap.has(cityLower)) {
                // Store with proper capitalization
                const properCity = city
                  .trim()
                  .toLowerCase()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                cityMap.set(cityLower, properCity);
              }
            }
          });
          const uniqueCities = Array.from(cityMap.values()).sort();
          setAvailableCities(uniqueCities);
        }
      } catch (error) {
      } finally {
        setIsLoadingCities(false);
      }
    };

    fetchCities();

    // Auto-populate user details from props or URL parameters
    if (prefillData) {
      setUserDetails({
        uid: prefillData.uid || '',
        name: prefillData.name || '',
        email: prefillData.email || '',
        mobile: prefillData.mobile || ''
      });
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const prefill = urlParams.get('prefill');
      const urlLatitude = urlParams.get('latitude');
      const urlLongitude = urlParams.get('longitude');

      if (prefill === 'true') {
        const uid = urlParams.get('uid');
        const name = urlParams.get('name');
        const email = urlParams.get('email');
        const mobile = urlParams.get('mobile');

        if (name || email || mobile) {
          setUserDetails({
            uid: uid || '',
            name: name || '',
            email: email || '',
            mobile: mobile || ''
          });
        }
      }

      // Set initial filters if latitude and longitude are available from URL
      if (urlLatitude && urlLongitude) {
        setFilters(prev => ({
          ...prev,
          latitude: parseFloat(urlLatitude),
          longitude: parseFloat(urlLongitude)
        }));
      }
    }
  }, [companyLower]);



  // Never let the post-submit reset fire after the component has gone away
  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Reset the "request a dentist" form whenever a new search starts, so a second
  // no-result search shows the form again instead of a stale "Thank you".
  // Prefills with the pincode being searched (empty for a location-only search).
  const resetDentistRequest = (pincode) => {
    // Drop any pending post-submit reset, so it cannot fire mid-search later
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
    setRequestPincode((pincode || '').replace(/\D/g, '').slice(0, 6));
    setRequestSubmitted(false);
    setRequestSubmitting(false);
    setRequestError('');
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      pincode: '',
      city: '',
      doctorName: '',
      clinicName: '',
      latitude: null,
      longitude: null
    });
    setAppliedFilters({
      location: '',
      pincode: '',
      city: '',
      doctorName: '',
      clinicName: '',
      latitude: null,
      longitude: null
    });
    setHasSearched(false);
    setClinics([]);
    setTotalClinics(0);
    setClinicApiError("");
    setAppliedFilterType('pincode');
    resetDentistRequest('');
  };

  const handleLocationSearch = () => {
    if (locationPermissionDenied) {
      setClinicApiError("Location access was denied. To use this feature, please enable location services in your browser's settings and refresh the page.");
      return;
    }

    if (navigator.geolocation) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newFilters = {
            ...filters,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setFilters(newFilters);
          // Auto-trigger search immediately with location
          const payload = { company: companyName };
          payload.lat = position.coords.latitude;
          payload.long = position.coords.longitude;
          if (filters.pincode?.trim()) payload.pincode = filters.pincode.trim();
          if (filters.city?.trim()) payload.city = filters.city.trim();
          if (filters.clinicName?.trim()) payload.clinic_name = filters.clinicName.trim();
          if (filters.doctorName?.trim()) payload.doctor_name = filters.doctorName.trim();
          setIsLoadingClinics(true);
          setHasSearched(true);
          setClinicApiError('');
          setAppliedFilters({ ...newFilters });
          resetDentistRequest(filters.pincode);
          getInsuranceClinicAvailability(payload)
            .then((response) => {
              if (response.status === 200 && response.data?.data?.clinics) {
                const clinicsData = response.data.data.clinics;
                const mappedClinics = clinicsData.map(clinic => ({
                  id: clinic._id || clinic.id,
                  name: clinic.name,
                  doctor: clinic.doctor_name,
                  specialty: "General Dentistry",
                  address: clinic.clinic_address || clinic.address,
                  address_2: clinic.locality || clinic.address_2,
                  city: clinic.city,
                  state: clinic.state,
                  pincode: clinic.pincode,
                  working_hours: clinic.clinic_timing || clinic.working_hours,
                  sunday_hours: clinic.sunday_working_hours || clinic.sunday_hours,
                  distance: clinic.distance,
                  network_partner: {
                    name: clinic.doctor_name || clinic.network_partner?.name,
                    experience_years: clinic.experience_years || clinic.network_partner?.experience_years,
                    phone: clinic.contact_number || clinic.network_partner?.phone,
                    email: clinic.email || clinic.network_partner?.email
                  }
                }));
                const visibleClinics = filterDemoClinics(mappedClinics);
                setClinics(visibleClinics);
                setTotalClinics(showTestClinics ? (response.data.data.total || mappedClinics.length) : visibleClinics.length);
                setAppliedFilterType(response.data.data.filter_applied || 'pincode');
              } else {
                setClinics([]);
                setTotalClinics(0);
              }
            })
            .catch((err) => {
              if (err.response?.status === 404) {
                setClinics([]);
                setTotalClinics(0);
              } else {
                setClinicApiError('Failed to fetch clinics. Please try again.');
              }
            })
            .finally(() => { setIsLoadingClinics(false); setIsLocating(false); });
        },
        (error) => {
          setIsLocating(false);
          console.error("Error getting user location: ", error);
          let errorMessage = "Could not get your location. Please try again.";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "Location access denied. Please enable location services in your browser settings for better results.";
              setLocationPermissionDenied(true);
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information is unavailable. Please check your device settings.";
              break;
            case error.TIMEOUT:
              errorMessage = "The request to get user location timed out. Please try again.";
              break;
            default:
              errorMessage = "An unknown error occurred while fetching your location. Please try again.";
              break;
          }
          setClinicApiError(errorMessage);
        }
      );
    } else {
      setIsLocating(false);
      setClinicApiError("Geolocation is not supported by this browser.");
    }
  };

  const handleSearch = async () => {
    // Validate that at least one filter is provided
    if (
      !filters.pincode?.trim() &&
      !filters.city?.trim() &&
      !filters.clinicName?.trim() &&
      !filters.doctorName?.trim() &&
      (!filters.latitude || !filters.longitude)
    ) {
      setClinicApiError("Please enter a search criteria (pincode, city, clinic, doctor) or allow location access.");
      return;
    }

    setIsLoadingClinics(true);
    setClinicApiError("");
    setHasSearched(true);
    setClinics([]);
    resetDentistRequest(filters.pincode);

    // Build payload for availability API
    const payload = {
      page: 1,
      pageSize: 10
    };

    if (filters.pincode?.trim()) {
      payload.pincode = filters.pincode.trim();
    }

    if (filters.city?.trim()) {
      payload.city = filters.city.trim();
    }

    if (filters.clinicName?.trim()) {
      payload.clinic_name = filters.clinicName.trim();
    }

    if (filters.doctorName?.trim()) {
      payload.doctor_name = filters.doctorName.trim();
    }

    // Add latitude and longitude to payload if available
    if (filters.latitude && filters.longitude) {
      payload.lat = filters.latitude;
      payload.long = filters.longitude;
    } else if (appliedFilters.latitude && appliedFilters.longitude) {
      payload.lat = appliedFilters.latitude;
      payload.long = appliedFilters.longitude;
    }

    try {
      const response = await getInsuranceClinicAvailability(payload);

      if (response.status === 200 && response.data?.data?.clinics) {
        const clinicsData = response.data.data.clinics;
        const totalClinics = response.data.data.total;

        // Map API response to component structure
        const mappedClinics = clinicsData.map(clinic => ({
          id: clinic._id || clinic.id,
          name: clinic.name,
          doctor: clinic.doctor_name,
          specialty: "General Dentistry",
          address: clinic.clinic_address || clinic.address,
          address_2: clinic.locality || clinic.address_2,
          city: clinic.city,
          state: clinic.state,
          pincode: clinic.pincode,
          working_hours: clinic.clinic_timing || clinic.working_hours,
          sunday_hours: clinic.sunday_working_hours || clinic.sunday_hours,
          distance: clinic.distance,
          network_partner: {
            name: clinic.doctor_name || clinic.network_partner?.name,
            experience_years: clinic.experience_years || clinic.network_partner?.experience_years,
            phone: clinic.contact_number || clinic.network_partner?.phone,
            email: clinic.email || clinic.network_partner?.email
          }
        }));

        const visibleClinics = filterDemoClinics(mappedClinics);
        setClinics(visibleClinics);
        setTotalClinics(showTestClinics ? totalClinics : visibleClinics.length);

        // Copy current filters to applied filters
        const filtersToApply = { ...filters };
        setAppliedFilters(filtersToApply);
        setAppliedFilterType(response.data.data.filter_applied || 'pincode');
      } else if (response.status === 404 || response.data?.status === 404) {
        setClinics([]);
        setAppliedFilters({ ...filters });
        setTotalClinics(0);
      } else {
        setClinicApiError("Failed to load clinics. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching clinic availability:", error);
      if (error.response?.status === 404) {
        setClinics([]);
        setAppliedFilters({ ...filters });
        setTotalClinics(0);
      } else {
        setClinicApiError("Failed to load clinics. Please try again.");
      }
    } finally {
      setIsLoadingClinics(false);
    }
  };

  const isRequestPincodeValid = /^\d{6}$/.test(requestPincode);

  const handleDentistRequest = async () => {
    if (!isRequestPincodeValid || requestSubmitting) return;

    setRequestSubmitting(true);
    setRequestError('');

    try {
      await requestDentistByPincode({
        // Rendered into the "New dentist request" email to the network team
        pincode: requestPincode,
        name: userDetails.name || null,
        email: userDetails.email || null,
        phone: userDetails.mobile || null,
        requested_at: new Date().toISOString(),
        // Context for triage, not shown in the email
        uid: userDetails.uid || null,
        client: companyName,
        searched_pincode: appliedFilters.pincode || null,
        searched_city: appliedFilters.city || null,
        source: 'appointment_no_clinics'
      });

      // Confirm, then drop the member back on a clean search form so they can look
      // again or book elsewhere instead of being stranded on the thank-you card.
      setRequestSubmitted(true);
      resetTimerRef.current = setTimeout(() => clearFilters(), 2500);
    } catch (error) {
      console.error("Error submitting dentist request:", error);
      setRequestError("Could not submit your request. Please try again.");
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleBookAppointment = useCallback(async () => {
    if (selectedDate && selectedTime && selectedAppointmentType && selectedClinic && userDetails.name && userDetails.email && userDetails.mobile) {
      setBookingError("");
      setBookingLoading(true);

      // Demo login - never create a real appointment, always fake the booking
      // (checks the logged-in account, not the selected member, since demo members come from the DB)
      if (isDemoUser) {
        // Simulate loading
        await new Promise(resolve => setTimeout(resolve, 500));

        const appointment = {
          clinic: selectedClinic,
          date: selectedDate,
          time: selectedTime,
          appointmentType: selectedAppointmentType,
          userDetails: userDetails,
          bookingId: `DEMO${Date.now().toString().slice(-6)}`
        };
        setBookedAppointment(appointment);
        setShowBooking(false);
        setShowConfirmation(true);
        setSelectedDate('');
        setSelectedTime('');
        setSelectedAppointmentType('');
        setSelectedClinic(null);
        setUserDetails({ name: '', email: '', mobile: '' });
        setBookingLoading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('clinicId', selectedClinic.id);
        formData.append('clinic', JSON.stringify(selectedClinic));
        formData.append('client', companyName);
        formData.append('date', selectedDate);
        formData.append('time', selectedTime);
        formData.append('appointment_type', selectedAppointmentType);
        formData.append('name', userDetails.name);
        formData.append('email', userDetails.email);
        formData.append('mobile', userDetails.mobile);
        if (userDetails.uid) {
          formData.append('uid', userDetails.uid);
        }

        const response = await createInsuranceAppointment(formData);
        if (response.status === 200) {
          const appointment = {
            clinic: selectedClinic,
            date: selectedDate,
            time: selectedTime,
            appointmentType: selectedAppointmentType,
            userDetails: userDetails,
            bookingId: response.data?.bookingId || `HC${Date.now().toString().slice(-6)}`
          };
          setBookedAppointment(appointment);
          setShowBooking(false);
          setShowConfirmation(true);
          setSelectedDate('');
          setSelectedTime('');
          setSelectedAppointmentType('');
          setSelectedClinic(null);
          setUserDetails({ name: '', email: '', mobile: '' });
        } else {
          setBookingError("Internal server error. Please try again.");
        }
      } catch (e) {
        setBookingError("Internal server error. Please try again.");
      } finally {
        setBookingLoading(false);
      }
    }
  }, [selectedDate, selectedTime, selectedAppointmentType, selectedClinic, userDetails, companyName, isDemoUser]);

  // Clinics are now filtered by the API, so we just use the clinics state directly
  const filteredClinics = clinics;

  if (showConfirmation) {
    return (
      <ConfirmationModal
        showConfirmation={showConfirmation}
        setShowConfirmation={setShowConfirmation}
        bookedAppointment={bookedAppointment}
        setHasSearched={setHasSearched}
        companyName={companyName}
        isDark={isDark}
        onClose={onClose}
      />
    );
  }

  // Theme-aware colors
  const t = themeStyles[isDark ? 'dark' : 'light'];
  const bgPrimary = t['--bg-primary'];
  const bgSecondary = t['--bg-secondary'];
  const bgTertiary = t['--bg-tertiary'];
  const bgCard = t['--bg-card'];
  const bgInput = t['--bg-input'];
  const textPrimary = t['--text-primary'];
  const textSecondary = t['--text-secondary'];
  const textTertiary = t['--text-tertiary'];
  const borderColor = t['--border-color'];
  const borderLight = t['--border-light'];
  const shadowCard = t['--shadow-card'];
  const shadowCardHover = t['--shadow-card-hover'];

  const hasActiveFilters = filters.pincode || filters.city || filters.clinicName || filters.doctorName || filters.latitude || filters.longitude;

  // Both a pincode and a city were searched, but the API only ever applies one of them.
  // Read appliedFilters, not filters: this must describe the list on screen, not what is
  // currently typed. Skipped for location searches, where lat/long outranks both and the
  // filter bar already shows a "Location" chip.
  const showFilterNotice =
    Boolean(appliedFilters.pincode?.trim()) &&
    Boolean(appliedFilters.city?.trim()) &&
    !(appliedFilters.latitude && appliedFilters.longitude);

  const filterNoticeText = appliedFilterType === 'city'
    ? 'Showing all clinics in the city. To narrow to a Pin Code, clear the City filter.'
    : 'Showing clinics in Pin Code only. To see all clinics in the city, clear the Pin Code filter.';

  return (
    <div className="min-h-screen transition-colors duration-500" style={{ background: isDark ? 'linear-gradient(180deg, #0c1222 0%, #111827 100%)' : 'linear-gradient(180deg, #f0f2ff 0%, #f8f9fe 50%, #fafaff 100%)' }}>
      {/* Global CSS */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 500px; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes shake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-4px); } 40%,80% { transform: translateX(4px); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes bounce { 0%,100% { transform: translateY(0); opacity: 0.4; } 50% { transform: translateY(-4px); opacity: 1; } }
        @keyframes pulse-ring { 0% { transform: scale(0.95); opacity: 0.7; } 50% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(0.95); opacity: 0.7; } }
        .clinic-card { animation: fadeInUp 0.4s ease-out both; transition: transform 0.35s cubic-bezier(0.4,0,0.2,1), box-shadow 0.35s cubic-bezier(0.4,0,0.2,1), border-color 0.3s ease; }
        .clinic-card:nth-child(1) { animation-delay: 0.03s; }
        .clinic-card:nth-child(2) { animation-delay: 0.08s; }
        .clinic-card:nth-child(3) { animation-delay: 0.13s; }
        .clinic-card:nth-child(4) { animation-delay: 0.18s; }
        .clinic-card:nth-child(5) { animation-delay: 0.23s; }
        .clinic-card:hover { transform: translateY(-4px) scale(1.008); }
        @keyframes glow { 0%,100% { box-shadow: 0 0 6px rgba(99,102,241,0.15); } 50% { box-shadow: 0 0 14px rgba(99,102,241,0.25); } }
        .filter-input { transition: all 0.2s ease; }
        .filter-input:focus { box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
        .search-btn { transition: all 0.3s cubic-bezier(0.4,0,0.2,1); }
        .search-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(59,130,246,0.35); }
        .search-btn:active { transform: translateY(0) scale(0.98); }
        .loading-shimmer { background: linear-gradient(90deg, ${bgTertiary} 25%, ${borderColor} 50%, ${bgTertiary} 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
        .location-btn { transition: all 0.3s ease; }
        .location-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16,185,129,0.25); }
      `}</style>

      {/* ─── Header ─── */}
      <div className="sticky top-0 z-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #6366f1 100%)' }}>
        {/* Decorative circles */}
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10" style={{ background: 'rgba(255,255,255,0.15)' }}></div>
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-10" style={{ background: 'rgba(255,255,255,0.1)' }}></div>

        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-30 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="relative z-10 px-5 py-3">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg" style={{ animation: 'scaleIn 0.5s ease-out' }}>
              <img src={toothlensLogo} alt="ToothLens" className="w-6 h-6 rounded" />
            </div>
            <div style={{ animation: 'fadeInUp 0.4s ease-out' }}>
              <h1 className="text-lg font-bold text-white leading-tight">ToothLens</h1>
              <p className="text-blue-200 text-xs uppercase tracking-wide">Dental Network</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <h2 className="text-white font-bold text-base flex-1 text-center" style={{ animation: 'fadeInUp 0.5s ease-out' }}>
              Find Your Dentist &amp; Book Your Appointment
            </h2>
            <DarkModeToggle isDark={isDark} onToggle={() => setIsDark(!isDark)} />
          </div>
        </div>
      </div>

      {/* ─── Pre-search: Full Search Form Card ─── */}
      {!hasSearched && (
        <div className="px-4 py-6 flex-1 flex flex-col items-center" style={{ animation: 'fadeIn 0.5s ease-out' }}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{
            background: bgCard,
            boxShadow: shadowCard,
            borderTop: '4px solid transparent',
            borderImage: 'linear-gradient(90deg, #6366f1, #3b82f6, #6366f1) 1',
          }}>
            <div className="p-5 space-y-5">
              {/* ── Current Location Section ── */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: textPrimary }}>
                  Current Location
                </label>
                <button
                  onClick={handleLocationSearch}
                  disabled={isLocating}
                  className="location-btn w-full flex items-center justify-center space-x-2 py-3 rounded-xl font-semibold text-sm"
                  style={{
                    background: isLocating ? '#f0fdf4' : '#f0fdf4',
                    border: '2px solid #10b981',
                    color: '#059669',
                  }}
                >
                  {isLocating ? (
                    <>
                      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ animation: 'pulse-ring 1.2s ease-in-out infinite' }}>
                        <Navigation className="w-4 h-4" style={{ animation: 'spin 1.5s linear infinite' }} />
                      </div>
                      <span>Detecting location...</span>
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full" style={{ animation: 'bounce 1s ease-in-out infinite', animationDelay: '0s' }}></div>
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full" style={{ animation: 'bounce 1s ease-in-out infinite', animationDelay: '0.2s' }}></div>
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full" style={{ animation: 'bounce 1s ease-in-out infinite', animationDelay: '0.4s' }}></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <Crosshair className="w-4 h-4" />
                      <span>Use My Current Location</span>
                    </>
                  )}
                </button>
              </div>

              {/* ── Divider ── */}
              <div className="flex items-center space-x-3">
                <div className="flex-1 h-px" style={{ background: borderColor }}></div>
                <span className="text-xs font-medium uppercase tracking-wide" style={{ color: textTertiary }}>or search by details</span>
                <div className="flex-1 h-px" style={{ background: borderColor }}></div>
              </div>

              {/* ── Pincode ── */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: textPrimary }}>Pincode</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: textTertiary }} />
                  <input
                    type="text"
                    placeholder="Enter 6-digit pincode"
                    value={filters.pincode}
                    onChange={(e) => handleFilterChange('pincode', e.target.value)}
                    className="filter-input w-full pl-10 pr-3 py-3 rounded-xl text-sm focus:outline-none"
                    style={{ background: bgInput, border: `1.5px solid ${borderColor}`, color: textPrimary }}
                  />
                </div>
              </div>

              {/* ── City ── */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: textPrimary }}>City</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: textTertiary }} />
                  <select
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    className="filter-input w-full pl-10 pr-8 py-3 rounded-xl text-sm focus:outline-none appearance-none"
                    style={{ background: bgInput, border: `1.5px solid ${borderColor}`, color: filters.city ? textPrimary : textTertiary }}
                    disabled={isLoadingCities}
                  >
                    <option value="">{isLoadingCities ? 'Loading cities...' : 'Select your city'}</option>
                    {availableCities.map((city) => <option key={city} value={city}>{city}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: textTertiary }} />
                </div>
              </div>

              {/* ── Clinic Name ── */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: textPrimary }}>Clinic Name</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: textTertiary }} />
                  <input
                    type="text"
                    placeholder="Type clinic name..."
                    value={filters.clinicName}
                    onChange={(e) => handleFilterChange('clinicName', e.target.value)}
                    className="filter-input w-full pl-10 pr-3 py-3 rounded-xl text-sm focus:outline-none"
                    style={{ background: bgInput, border: `1.5px solid ${borderColor}`, color: textPrimary }}
                  />
                </div>
              </div>

              {/* ── Doctor Name ── */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: textPrimary }}>Doctor Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: textTertiary }} />
                  <input
                    type="text"
                    placeholder="Type doctor name..."
                    value={filters.doctorName}
                    onChange={(e) => handleFilterChange('doctorName', e.target.value)}
                    className="filter-input w-full pl-10 pr-3 py-3 rounded-xl text-sm focus:outline-none"
                    style={{ background: bgInput, border: `1.5px solid ${borderColor}`, color: textPrimary }}
                  />
                </div>
              </div>

              {/* ── Search Button ── */}
              <button
                onClick={handleSearch}
                className="search-btn w-full text-white py-3.5 rounded-xl font-semibold text-base flex items-center justify-center space-x-2"
                style={{
                  background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
                  boxShadow: '0 4px 15px rgba(99,102,241,0.35)',
                }}
              >
                <Search className="w-5 h-5" />
                <span>Search Clinics</span>
              </button>
            </div>
          </div>

          {/* Error message */}
          {clinicApiError && (
            <div className="w-full max-w-md mt-4 text-center py-3 px-4 rounded-xl text-sm font-medium" style={{
              background: isDark ? '#371520' : '#fef2f2',
              border: `1px solid ${isDark ? '#5b2130' : '#fecaca'}`,
              color: '#ef4444',
              animation: 'shake 0.5s ease-out'
            }}>
              {clinicApiError}
            </div>
          )}
        </div>
      )}

      {/* ─── Post-search: Sticky Filter Bar ─── */}
      {hasSearched && (
        <div className="sticky top-0 z-20 transition-colors duration-500 border-b" style={{ background: bgPrimary, borderColor }}>
          <div className="px-4 py-2 space-y-2">
            {/* Compact filter row: Pincode + City + Search */}
            <div className="flex items-center space-x-2">
              <div className="flex-1 relative">
                <Hash className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5" style={{ color: textTertiary }} />
                <input
                  type="text"
                  placeholder="Pincode"
                  value={filters.pincode}
                  onChange={(e) => handleFilterChange('pincode', e.target.value)}
                  className="filter-input w-full pl-8 pr-2 py-2 rounded-lg text-sm focus:outline-none"
                  style={{ background: bgInput, border: `1px solid ${borderColor}`, color: textPrimary }}
                />
              </div>
              <div className="flex-1 relative">
                <MapPin className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5" style={{ color: textTertiary }} />
                <select
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  className="filter-input w-full pl-8 pr-2 py-2 rounded-lg text-sm focus:outline-none appearance-none"
                  style={{ background: bgInput, border: `1px solid ${borderColor}`, color: textPrimary }}
                  disabled={isLoadingCities}
                >
                  <option value="">{isLoadingCities ? 'Loading...' : 'City'}</option>
                  {availableCities.map((city) => <option key={city} value={city}>{city}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 pointer-events-none" style={{ color: textTertiary }} />
              </div>
              <button onClick={handleSearch}
                className="search-btn text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center space-x-1 whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                <Search className="w-3.5 h-3.5" /><span>Search Clinics</span>
              </button>
            </div>

            {/* More Filters + Clear row */}
            <div className="flex items-center justify-between">
              <button onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-1 text-xs font-medium transition-colors" style={{ color: '#6366f1' }}>
                <Filter className="w-3 h-3" />
                <span>{showFilters ? 'Hide' : 'More'}</span>
                {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              <div className="flex items-center space-x-2">
                {filters.latitude && filters.longitude && (
                  <span className="flex items-center text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: isDark ? '#064e3b' : '#d1fae5', color: isDark ? '#6ee7b7' : '#065f46' }}>
                    <Crosshair className="w-3 h-3 mr-1" />Location
                  </span>
                )}
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="flex items-center text-xs font-medium text-red-500">
                    <RotateCcw className="w-3 h-3 mr-0.5" />Reset
                  </button>
                )}
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="grid grid-cols-2 gap-2 pt-1.5 border-t" style={{ borderColor }}>
                <div className="relative">
                  <Building2 className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5" style={{ color: textTertiary }} />
                  <input type="text" placeholder="Clinic" value={filters.clinicName}
                    onChange={(e) => handleFilterChange('clinicName', e.target.value)}
                    className="filter-input w-full pl-8 pr-2 py-2 rounded-lg text-sm focus:outline-none"
                    style={{ background: bgInput, border: `1px solid ${borderColor}`, color: textPrimary }} />
                </div>
                <div className="relative">
                  <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5" style={{ color: textTertiary }} />
                  <input type="text" placeholder="Doctor" value={filters.doctorName}
                    onChange={(e) => handleFilterChange('doctorName', e.target.value)}
                    className="filter-input w-full pl-8 pr-2 py-2 rounded-lg text-sm focus:outline-none"
                    style={{ background: bgInput, border: `1px solid ${borderColor}`, color: textPrimary }} />
                </div>
              </div>
            )}
          </div>

          {/* Available Clinics count */}
          {!isLoadingClinics && !clinicApiError && filteredClinics.length > 0 && (
            <div className="px-4 py-1.5 border-t" style={{ borderColor, background: isDark ? 'rgba(12,18,34,0.95)' : 'rgba(250,250,255,0.95)' }}>
              <h2 className="text-sm font-bold flex items-center" style={{ color: textPrimary }}>
                <Building2 className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                Available Clinics
                <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: isDark ? '#1e3a5f' : '#dbeafe', color: isDark ? '#60a5fa' : '#1e40af' }}>{totalClinics}</span>
              </h2>
            </div>
          )}
        </div>
      )}

      {/* ─── Results Section ─── */}
      {hasSearched && (
      <div className="px-4 py-4 flex-1">
        {isLoadingClinics ? (
          /* ── Loading Skeleton ── */
          <div className="space-y-3 py-2">
            <div className="flex items-center mb-2"><div className="h-5 w-36 loading-shimmer rounded-lg"></div></div>
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl p-4 space-y-3" style={{ background: bgCard, border: `1px solid ${borderColor}`, animation: `fadeIn ${0.2 + i * 0.15}s ease-out` }}>
                <div className="flex items-start space-x-3">
                  <div className="w-11 h-11 loading-shimmer rounded-xl"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 loading-shimmer rounded w-3/4"></div>
                    <div className="h-3 loading-shimmer rounded w-1/2"></div>
                  </div>
                </div>
                <div className="h-8 loading-shimmer rounded-xl w-full"></div>
                <div className="h-3 loading-shimmer rounded w-full"></div>
                <div className="h-10 loading-shimmer rounded-xl w-full"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Clinic Cards */}

            {/* Error */}
            {clinicApiError ? (
              <div className="text-center py-10" style={{ animation: 'fadeInUp 0.4s ease-out' }}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: isDark ? '#371520' : '#fee2e2' }}>
                  <X className="w-7 h-7 text-red-400" />
                </div>
                <h3 className="text-sm font-semibold text-red-500 mb-1">{clinicApiError}</h3>
                <button onClick={handleSearch} className="mt-3 text-blue-500 text-sm font-medium hover:underline">Try Again</button>
              </div>
            ) : (
              <>
                {/* ── Which filter is actually in effect ── */}
                {showFilterNotice && filteredClinics.length > 0 && (
                  <div
                    className="flex items-start space-x-2 mb-4 px-3.5 py-2.5 rounded-xl text-xs leading-relaxed"
                    style={{
                      background: isDark ? 'rgba(99,102,241,0.10)' : '#eef0fd',
                      border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#dfe3fb'}`,
                      color: isDark ? '#a5b4fc' : '#3730a3',
                      animation: 'fadeInUp 0.4s ease-out'
                    }}
                  >
                    <Info className="w-4 h-4 flex-shrink-0 mt-px" />
                    <span>{filterNoticeText}</span>
                  </div>
                )}

                {/* ── Clinic Cards Grid ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
                  {filteredClinics.map((clinic, index) => {
                    const accentColors = [
                      ['#6366f1','#818cf8'], ['#3b82f6','#60a5fa'], ['#8b5cf6','#a78bfa'],
                      ['#0ea5e9','#38bdf8'], ['#6366f1','#818cf8'], ['#3b82f6','#60a5fa']
                    ];
                    const [accentFrom, accentTo] = accentColors[index % accentColors.length];
                    return (
                    <div key={clinic.id}
                      className="clinic-card rounded-2xl overflow-hidden"
                      style={{
                        background: isDark
                          ? 'linear-gradient(145deg, rgba(20,28,48,0.95), rgba(28,39,66,0.9))'
                          : bgCard,
                        border: `1px solid ${isDark ? 'rgba(99,102,241,0.12)' : borderColor}`,
                        boxShadow: isDark ? `${shadowCard}, inset 0 1px 0 rgba(255,255,255,0.03)` : shadowCard,
                        animationDelay: `${index * 0.05}s`,
                        backdropFilter: isDark ? 'blur(10px)' : 'none'
                      }}>
                      {/* Accent top strip */}
                      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${accentFrom}, ${accentTo})` }}></div>
                      <div className="p-4">
                        {/* Card Header */}
                        <div className="flex items-start space-x-3 mb-3">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{
                              background: `linear-gradient(135deg, ${accentFrom}, ${accentTo})`,
                              boxShadow: `0 4px 12px ${accentFrom}33`
                            }}>
                            <Building2 className="w-5.5 h-5.5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-sm leading-tight mb-0.5" style={{ color: textPrimary }}>{getHighlightedText(clinic.name, appliedFilters.clinicName)}</h3>
                            <p className="font-medium text-xs flex items-center" style={{ color: isDark ? '#818cf8' : '#4f46e5' }}>
                              <User className="w-3 h-3 mr-1" />{getHighlightedText(clinic.doctor, appliedFilters.doctorName)}
                            </p>
                            {clinic.network_partner?.experience_years && (
                              <div className="flex items-center mt-1">
                                <div className="flex items-center text-xs px-2 py-0.5 rounded-md" style={{ background: isDark ? 'rgba(99,102,241,0.1)' : '#f0f0ff', color: isDark ? '#a5b4fc' : '#6366f1' }}>
                                  <Star className="w-3 h-3 mr-1" />{clinic.network_partner.experience_years} yrs exp
                                </div>
                              </div>
                            )}
                          </div>
                          {clinic.distance && (
                            <span className="text-xs font-bold px-2.5 py-1 rounded-lg whitespace-nowrap" style={{
                              background: isDark ? 'rgba(59,130,246,0.12)' : '#eef2ff',
                              color: isDark ? '#93c5fd' : '#4338ca',
                              border: `1px solid ${isDark ? 'rgba(59,130,246,0.2)' : '#e0e7ff'}`
                            }}>
                              {Math.round(clinic.distance)} km
                            </span>
                          )}
                        </div>

                        {/* Timing */}
                        {clinic.working_hours && (
                          <div className="flex items-center text-xs p-2.5 rounded-xl mb-3" style={{
                            background: isDark ? 'rgba(16,185,129,0.08)' : 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
                            border: `1px solid ${isDark ? 'rgba(52,211,153,0.15)' : '#d1fae5'}`
                          }}>
                            <div className="w-6 h-6 rounded-lg flex items-center justify-center mr-2 flex-shrink-0" style={{
                              background: isDark ? 'rgba(52,211,153,0.15)' : '#d1fae5'
                            }}>
                              <Clock className="w-3.5 h-3.5" style={{ color: isDark ? '#6ee7b7' : '#059669' }} />
                            </div>
                            <div className="flex-1">
                              <span className="font-medium" style={{ color: isDark ? '#a7f3d0' : '#047857' }}>{clinic.working_hours}</span>
                              {clinic.sunday_hours && (
                                <span className="ml-1.5" style={{ color: clinic.sunday_hours === 'Closed' ? '#f87171' : (isDark ? '#6ee7b7' : '#059669') }}>
                                  · Sun: {clinic.sunday_hours}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Address */}
                        <div className="flex items-start text-xs mb-4 p-2 rounded-lg" style={{
                          background: isDark ? 'rgba(255,255,255,0.02)' : '#f8f9fe',
                          color: textSecondary
                        }}>
                          <div className="w-5 h-5 rounded-md flex items-center justify-center mr-2 mt-0.5 flex-shrink-0" style={{
                            background: isDark ? 'rgba(139,158,195,0.1)' : '#eef1f8'
                          }}>
                            <MapPin className="w-3 h-3" style={{ color: textTertiary }} />
                          </div>
                          <span className="leading-relaxed">
                            {clinic.address}{clinic.address_2 && `, ${clinic.address_2}`}{clinic.city && `, ${clinic.city}`}{clinic.pincode && ` – ${clinic.pincode}`}
                          </span>
                        </div>

                        {/* Book Button */}
                        <button
                          onClick={() => {
                            if (window.confirm("You're about to schedule an appointment with " + clinic.name + ". Would you like to continue?")) {
                              setSelectedClinic(clinic);
                              setSelectedDate('');
                              setSelectedTime('');
                              setShowBooking(true);
                            }
                          }}
                          className="w-full text-white py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center space-x-2 group"
                          style={{
                            background: `linear-gradient(135deg, ${accentFrom}, ${accentTo})`,
                            boxShadow: `0 4px 14px ${accentFrom}30`
                          }}>
                          <Calendar className="w-4 h-4" />
                          <span>Book Appointment</span>
                          <ArrowRight className="w-4 h-4 transition-transform duration-300" style={{ transform: 'translateX(0)' }} />
                        </button>
                      </div>
                    </div>
                  );})}
                </div>

                {/* ── Empty results: refer a dentist / request one in a pincode ── */}
                {filteredClinics.length === 0 && (
                  <div
                    className="w-full max-w-md mx-auto rounded-2xl text-center px-7 py-9 my-6"
                    style={{ background: bgCard, boxShadow: shadowCard, animation: 'fadeInUp 0.4s ease-out' }}
                  >
                    {!requestSubmitted ? (
                      <>
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: bgTertiary }}>
                          <MapPin className="w-6 h-6" style={{ color: textTertiary }} />
                        </div>
                        <h3 className="text-base font-bold mb-1.5" style={{ color: textPrimary }}>
                          We're adding more clinics in your area
                        </h3>
                        <p className="text-xs leading-relaxed mb-6" style={{ color: textSecondary }}>
                          In the meantime, if you have a preferred dentist, please refer them to us.
                          Our team will connect with your dentist and enable cashless services for you!
                        </p>

                        {canClaimReimbursement && (
                          <div
                            className="rounded-xl p-4 mb-5 text-left"
                            style={{ background: bgTertiary, border: `1.5px solid ${borderColor}` }}
                            data-testid="reimbursement-option"
                          >
                            <p className="text-sm font-bold mb-1" style={{ color: textPrimary }}>
                              Need treatment now?
                            </p>
                            <p className="text-xs leading-relaxed mb-3" style={{ color: textSecondary }}>
                              Consult any dentist, get their prescription approved by us, and claim a reimbursement after your treatment.
                            </p>
                            <button
                              onClick={() => navigate('/phonepe/dashboard?open=reimbursement')}
                              className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl font-semibold text-sm text-white"
                              style={{ background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)' }}
                            >
                              <ReceiptText className="w-4 h-4" />
                              <span>Consult any dentist &amp; claim reimbursement</span>
                            </button>
                          </div>
                        )}

                        <button
                          onClick={() => setShowReferDentist(true)}
                          className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl font-semibold text-sm"
                          style={{ border: `1.5px solid ${borderColor}`, color: textPrimary }}
                        >
                          <Stethoscope className="w-4 h-4" />
                          <span>Refer your dentist</span>
                        </button>

                        {/* ── Divider ── */}
                        <div className="flex items-center space-x-3 my-5">
                          <div className="flex-1 h-px" style={{ background: borderColor }}></div>
                          <span className="text-xs font-medium" style={{ color: textTertiary }}>or</span>
                          <div className="flex-1 h-px" style={{ background: borderColor }}></div>
                        </div>

                        <p className="text-xs leading-relaxed mb-3" style={{ color: textSecondary }}>
                          Don't have a preferred dentist? Request one in your pincode.
                        </p>

                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="Enter pincode"
                            value={requestPincode}
                            onChange={(e) => setRequestPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="filter-input flex-1 px-3.5 py-2.5 rounded-xl text-sm focus:outline-none"
                            style={{ background: bgInput, border: `1.5px solid ${borderColor}`, color: textPrimary }}
                          />
                          <button
                            onClick={handleDentistRequest}
                            disabled={!isRequestPincodeValid || requestSubmitting}
                            className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white whitespace-nowrap"
                            style={
                              !isRequestPincodeValid || requestSubmitting
                                ? { background: textTertiary, cursor: 'not-allowed' }
                                : { background: 'linear-gradient(135deg, #4f46e5, #6366f1)', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' }
                            }
                          >
                            {requestSubmitting ? 'Submitting…' : 'Submit'}
                          </button>
                        </div>

                        {requestError && (
                          <div className="mt-3 text-center py-2.5 px-3 rounded-xl text-xs font-medium" style={{
                            background: isDark ? '#371520' : '#fef2f2',
                            border: `1px solid ${isDark ? '#5b2130' : '#fecaca'}`,
                            color: '#ef4444',
                            animation: 'shake 0.5s ease-out'
                          }}>
                            {requestError}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                          background: isDark ? 'rgba(16,185,129,0.12)' : '#eaf6ef'
                        }}>
                          <Check className="w-6 h-6" style={{ color: isDark ? '#6ee7b7' : '#1d7a4c' }} />
                        </div>
                        <h3 className="text-base font-bold mb-1.5" style={{ color: textPrimary }}>
                          Thank you for your referral
                        </h3>
                        <p className="text-xs leading-relaxed" style={{ color: textSecondary }}>
                          Our network will be updated based on your preference shortly.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      )}

      {/* Booking Modal */}
      <BookingModal
        showBooking={showBooking} setShowBooking={setShowBooking}
        selectedClinic={selectedClinic} userDetails={userDetails} setUserDetails={setUserDetails}
        selectedDate={selectedDate} setSelectedDate={setSelectedDate}
        selectedTime={selectedTime} setSelectedTime={setSelectedTime}
        selectedAppointmentType={selectedAppointmentType} setSelectedAppointmentType={setSelectedAppointmentType}
        handleBookAppointment={handleBookAppointment} bookingLoading={bookingLoading}
        bookingError={bookingError} timeSlots={getAvailableTimeSlots(selectedDate)} isDark={isDark}
      />

      <ReferDentistModal
        show={showReferDentist} onClose={() => setShowReferDentist(false)}
        userDetails={userDetails} companyName={companyName} isDark={isDark}
      />
    </div>
  );
};

export default PlumAppointment;